import { useEffect, useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import axios from "axios";

export enum ProductKey {
  BRICKZ = "dataBrickz",
}

export enum FeatureKey {
  ACCESS = "allowAccess",
}

export type FeatureValue = boolean | string;

export type AgentEntitlements = Partial<
  Record<ProductKey, Partial<Record<FeatureKey, FeatureValue>>>
>;

interface BrickzEntitlementsResponse {
  hasAccess: boolean;
}

interface GetUserJwtResult {
  getUserJWT: { idToken: string | null } | null;
}

const GET_USER_JWT_QUERY = gql`
  query GetUserJWT {
    getUserJWT {
      idToken
    }
  }
`;

const AGENT_INSIGHTS_HOST =
  process.env.REACT_APP_AGENT_INSIGHTS_HOST ||
  "https://agent-insights.integration.propertyguru.com";

const toAuthorizationHeader = (idToken: string): string => {
  const trimmed = idToken.trim();
  return /^Bearer\s+/i.test(trimmed) ? trimmed : `Bearer ${trimmed}`;
};

export const fetchAgentEntitlements = async (
  agentId: string | number,
  jwt: string
): Promise<AgentEntitlements> => {
  try {
    const { data } = await axios.get<BrickzEntitlementsResponse>(
      `${AGENT_INSIGHTS_HOST}/v1/brickz/${agentId}/entitlements`,
      {
        headers: { Authorization: toAuthorizationHeader(jwt) },
        params: { region: "my", agentId, brand: "ipp" },
      }
    );

    return {
      [ProductKey.BRICKZ]: { [FeatureKey.ACCESS]: Boolean(data?.hasAccess) },
    };
  } catch {
    return {};
  }
};

export const hasEntitlement = (
  entitlements: AgentEntitlements,
  productKey: ProductKey,
  featureKey: FeatureKey
): boolean => {
  const value = entitlements[productKey]?.[featureKey];
  if (typeof value === "string") {
    return value.length > 0 && value.toLowerCase() !== "false";
  }
  return Boolean(value);
};

export const useAgentEntitlement = (
  agentId: string | number | null | undefined,
  productKey: ProductKey,
  featureKey: FeatureKey
): boolean => {
  const [isEntitled, setIsEntitled] = useState(false);
  const { refetch: getUserJwt } = useQuery<GetUserJwtResult>(
    GET_USER_JWT_QUERY,
    { fetchPolicy: "no-cache", skip: true }
  );

  useEffect(() => {
    setIsEntitled(false);
    if (!agentId) return;
    let cancelled = false;

    (async () => {
      try {
        const { data } = await getUserJwt();
        const jwt = data?.getUserJWT?.idToken;
        if (!jwt) return;

        const entitlements = await fetchAgentEntitlements(agentId, jwt);
        if (cancelled) return;
        setIsEntitled(hasEntitlement(entitlements, productKey, featureKey));
      } catch {
        // fetchAgentEntitlements already swallows errors; this guards the JWT call
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [agentId, productKey, featureKey]);

  return isEntitled;
};
